import { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { categoryColorMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
          { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类', item: `${window.location.origin}/category/${activeSlug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: currentCategory?.name || '分类页面',
        description: currentCategory?.description || '',
        url: `${window.location.origin}/category/${activeSlug}`,
      },
    ],
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <p className="text-foreground-500">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const colors = categoryColorMap[currentCategory.color];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-24 pb-10">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="px-4 md:px-6 lg:px-8 mt-6">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.badge} text-white`}>
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-nowrap">
              {categories.map((cat) => {
                const catColors = categoryColorMap[cat.color];
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`shrink-0 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                      activeSlug === cat.slug
                        ? `${catColors.badge} text-white`
                        : `bg-background-100 text-foreground-600 hover:bg-background-200`
                    }`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <article
                    key={article.id}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.01]"
                  >
                    <Link to={`/article/${article.id}`} className="block">
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.imageAlt}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          title={article.imageAlt}
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                          <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views.toLocaleString()}</span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}